import { useEffect, useMemo, type ReactElement } from "react"
import { UserIcon, Building } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"

import { Separator } from "@f2e/ui/components/base"

import { SETTINGS_ENDPOINTS } from "@/constants"
import { useSysConfig } from "@/hooks"
import { useStore } from "@/store"
import { Permission } from "@/types"

import { SidebarNav, SidebarNavItem, MobileSidebar } from "./components"
import {
  AccountProfileTab,
  OrganizationGeneralTab,
  OrganizationPlanTab,
  OrganizationUsersTab,
  OrganizationMetricsTab,
  UserMetricsTab,
} from "./tabs"
import WorkspaceListPage from "../Workspace/WorkspaceList"
import BrandList from "../Brand/BrandList"

const Settings = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { billingUiEnabled } = useSysConfig()
  const [currentOrganization] = useStore((state) => [state.currentOrganization])
  const isBillingUiEnabled = billingUiEnabled ?? true

  // Check if user has permission to view organization metrics
  const hasOrgMetricsPermission =
    currentOrganization?.myPermissions?.has(Permission.OrgViewMetrics) || false

  // Create organization nav items based on permissions
  const organizationNavItems = useMemo(() => {
    const items: SidebarNavItem[] = [
      {
        title: t("settings.nav.general"),
        href: SETTINGS_ENDPOINTS.organizationGeneral,
      },
      ...(isBillingUiEnabled
        ? [
            {
              title: t("settings.nav.plan"),
              href: SETTINGS_ENDPOINTS.organizationPlan,
            },
          ]
        : []),
      {
        title: t("settings.nav.users"),
        href: SETTINGS_ENDPOINTS.organizationUsers,
      },
      {
        title: t("settings.nav.workspaces"),
        href: SETTINGS_ENDPOINTS.organizationWorkspaces,
      },
      {
        title: t("settings.nav.brands"),
        href: SETTINGS_ENDPOINTS.organizationBrands,
      },
    ]

    if (hasOrgMetricsPermission) {
      items.push({
        title: t("settings.nav.metrics"),
        href: SETTINGS_ENDPOINTS.organizationMetrics,
      })
    }

    return items
  }, [hasOrgMetricsPermission, isBillingUiEnabled, t])

  const tabPages = useMemo(() => {
    const pages: Record<string, ReactElement> = {
      [SETTINGS_ENDPOINTS.accountProfile]: <AccountProfileTab />,
      [SETTINGS_ENDPOINTS.accountMetrics]: <UserMetricsTab />,
      [SETTINGS_ENDPOINTS.organizationGeneral]: <OrganizationGeneralTab />,
      [SETTINGS_ENDPOINTS.organizationUsers]: <OrganizationUsersTab />,
      [SETTINGS_ENDPOINTS.organizationWorkspaces]: <WorkspaceListPage />,
      [SETTINGS_ENDPOINTS.organizationBrands]: <BrandList />,
      [SETTINGS_ENDPOINTS.organizationMetrics]: <OrganizationMetricsTab />,
    }
    if (isBillingUiEnabled) {
      pages[SETTINGS_ENDPOINTS.organizationPlan] = <OrganizationPlanTab />
    }
    return pages
  }, [isBillingUiEnabled])

  useEffect(() => {
    if (isBillingUiEnabled) {
      return
    }
    if (location.pathname === SETTINGS_ENDPOINTS.organizationPlan) {
      navigate(SETTINGS_ENDPOINTS.organizationGeneral, { replace: true })
    }
  }, [isBillingUiEnabled, location.pathname, navigate])

  const activePath =
    !isBillingUiEnabled &&
    location.pathname === SETTINGS_ENDPOINTS.organizationPlan
      ? SETTINGS_ENDPOINTS.organizationGeneral
      : location.pathname

  const sidebarNavItems: SidebarNavItem[] = [
    {
      title: t("settings.nav.organization"),
      href: "",
      icon: Building,
      children: organizationNavItems,
    },
    {
      title: t("settings.nav.account"),
      href: "",
      icon: UserIcon,
      children: [
        {
          title: t("settings.nav.profile"),
          href: SETTINGS_ENDPOINTS.accountProfile,
        },
        {
          title: t("settings.nav.metrics"),
          href: SETTINGS_ENDPOINTS.accountMetrics,
        },
      ],
    },
  ]

  return (
    <>
      <div className="space-y-6 px-2 pb-10">
        <div className="space-y-0.5">
          <h2 className="text-2xl font-bold tracking-tight">
            {t("settings.title")}
          </h2>
          <p className="text-muted-foreground">{t("settings.description")}</p>
        </div>
        <Separator className="my-6" />

        <div className="block sm:hidden!">
          <MobileSidebar items={sidebarNavItems} />
        </div>

        <div className="flex flex-row gap-6">
          <aside className="hidden sm:block w-1/5 min-w-24">
            <SidebarNav items={sidebarNavItems} />
          </aside>
          <div className="h-full w-full max-w-(--breakpoint-xl) px-0 md:px-2">
            {tabPages[activePath]}
          </div>
        </div>
      </div>
    </>
  )
}

export default Settings
